import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import config from '../config';
import SponsorContainer from '../components/SponsorContainer';
import BetweenAds from '../components/BetweenAds';
import AgeGateModal from '../components/AgeGateModal';
import SeoHead from '../components/SeoHead';
import { carPath } from '../utils/carUrl';
import '../styles/ads.css';

const Home = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAgeRestriction, setShowAgeRestriction] = useState(false);
  const [pendingCar, setPendingCar] = useState(null);
  const scrollPositionRestored = useRef(false);

  useEffect(() => {
    fetchCars();
    // Drop legacy forever-flag so old sessions still see the new gate
    try {
      localStorage.removeItem('ageConfirmed');
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!loading && cars.length > 0 && !scrollPositionRestored.current) {
      const savedPosition = sessionStorage.getItem('homeScrollPosition');
      if (savedPosition) {
        setTimeout(() => {
          window.scrollTo(0, parseInt(savedPosition, 10));
          scrollPositionRestored.current = true;
        }, 100);
      }
    }
  }, [loading, cars]);

  const fetchCars = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/api/cars?status=approved`);
      setCars(response.data);
    } catch (error) {
      console.error('Xatolik:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCar = (car) => {
    sessionStorage.setItem('homeScrollPosition', window.scrollY.toString());
    navigate(carPath(car));
  };

  // Always show disclaimer before opening a card
  const handleViewDetails = (car) => {
    setPendingCar(car);
    setShowAgeRestriction(true);
  };

  return (
    <>
      <SeoHead
        title="Avto Tarix — avtomobil tarixi va DTP"
        description="Avtomobil davlat raqami bo'yicha DTP va tarixni tekshirish. Yangi yozuvlar arxivi."
        canonical="https://avto-tarix.uz/"
      />
      <section className="hero" aria-label="Avto Tarix">
        <div className="hero__inner">
          <div>
            <p className="hero__brand">
              Avto <span>Tarix</span>
            </p>
            <h1 className="hero__headline">{t('home.heroHeadline')}</h1>
            <p className="hero__lead">{t('home.heroLead')}</p>
            <div className="hero__actions">
              <Link to="/search" className="btn btn-primary">
                {t('home.searchCar')}
              </Link>
              <Link to="/add" className="btn btn-ghost">
                {t('home.addCar')}
              </Link>
            </div>
          </div>

          <div className="hero__visual" aria-hidden="true">
            <img
              className="plate plate--photo"
              src="/images/uz-plate-sample.png"
              alt="O‘zbekiston davlat raqami namuna: 01 A 123 BC"
              width={520}
              height={140}
              decoding="async"
            />
          </div>
        </div>
      </section>

      <SponsorContainer>
        <div className="container section">
          <div className="notice notice--warn">
            <div>
              <div className="notice__title">{t('home.ageWarning')}</div>
              <div className="notice__text">{t('home.ageDisclaimer')}</div>
            </div>
          </div>

          <p className="service-blurb">
            {t('home.serviceDescription')}{' '}
            <a href="https://my.gov.uz/ru/service/621" target="_blank" rel="noopener noreferrer">
              {t('home.serviceDescriptionLink')}
            </a>{' '}
            {t('home.serviceDescriptionEnd')}
          </p>

          <div className="section__head">
            <h2 className="section__title">{t('home.recentCars')}</h2>
            <p className="section__note">{t('home.archiveNote')}</p>
          </div>

          {loading ? (
            <div className="loading">
              <div className="spinner" />
              <p>{t('common.loading')}</p>
            </div>
          ) : cars.length === 0 ? (
            <div className="empty-state">
              <h3>{t('home.noCars')}</h3>
              <p>{t('home.noCarsDescription')}</p>
              <Link to="/add" className="btn btn-primary">
                {t('home.addCar')}
              </Link>
            </div>
          ) : (
            <div className="archive-grid">
              <BetweenAds cars={cars} onViewDetails={handleViewDetails} />
            </div>
          )}
        </div>
      </SponsorContainer>

      <AgeGateModal
        open={showAgeRestriction}
        onCancel={() => {
          setShowAgeRestriction(false);
          setPendingCar(null);
        }}
        onConfirm={() => {
          setShowAgeRestriction(false);
          if (pendingCar) {
            const car = pendingCar;
            setPendingCar(null);
            openCar(car);
          }
        }}
      />
    </>
  );
};

export default Home;
